import React from 'react';
import classNames from 'classnames';
import { usePatchRequestPriorityMutation } from 'hooks/queue/usePatchRequestPriorityMutation';
import { useQueueContext } from '../QueueContext';

interface QueueTogglePriorityButtonProps {
  id: string;
  currentPriority: 'Blocker' | 'Normal';
  onSuccess?: (newPriority: 'Blocker' | 'Normal') => void;
  /**
   * When true, only allow changing from 'Blocker' to 'Normal', not the other way around.
   */
  unflagOnly?: boolean;
}

export const QueueTogglePriorityButton: React.FC<QueueTogglePriorityButtonProps> = ({
  id,
  currentPriority,
  onSuccess,
  unflagOnly = false,
}) => {
  const nextPriority = currentPriority === 'Blocker' ? 'Normal' : 'Blocker';
  const label = currentPriority === 'Blocker' ? 'Unflag' : 'Flag as Blocker';
  const {
    dispatch,
    state: { requests },
  } = useQueueContext();

  const mutation = usePatchRequestPriorityMutation(data => {
    if (onSuccess) onSuccess(nextPriority);
    const trxExists = requests.some(req => req.id === data?.id);

    if (trxExists) {
      // Replace the existing trx
      dispatch({
        type: 'SET_REQUESTS',
        payload: requests.map(req => (req.id === data?.id ? data : req)),
      });
      dispatch({
        type: 'SET_SELECTED_ITEM',
        payload: data,
      });
    } else {
      // Add trx as a new request
      dispatch({
        type: 'SET_REQUESTS',
        payload: [...requests, data],
      });
    }
  });

  const handleClick = () => {
    // If unflagOnly is true, only allow changing from 'Blocker' to 'Normal'
    if (unflagOnly && currentPriority !== 'Blocker') {
      return;
    }

    mutation.mutate({ id, priority: nextPriority });
  };

  return (
    <div className="resolve-wrapper">
      <div className="resolved-button-wrapper">
        <button
          className={classNames('chat-input__mask-resolved', 'toggle-priority-button')}
          type="button"
          onClick={handleClick}
          disabled={mutation.isPending || (unflagOnly && currentPriority !== 'Blocker')}
          data-testid="toggle-priority-button"
        >
          {mutation.isPending ? 'Updating...' : label}
        </button>
      </div>
    </div>
  );
};

export default QueueTogglePriorityButton;
